import type { RequestPriority, RequestType } from '@feedback/shared';

/**
 * All widget user-facing copy, centralized in one module so a future i18n pass only has to
 * swap this file (e.g. resolve a locale and export a matching `Strings`-shaped object)
 * instead of hunting through components for hardcoded text. English-only for now.
 */
export interface Strings {
  tooltip: {
    /** Shown on launcher hover when the application configured no copy of its own. */
    defaultText: string;
  };
  modal: {
    /** The dialog's visible heading, also referenced by `aria-labelledby`. */
    heading: string;
    /** One line under the heading, on the navy header band. */
    subheading: string;
    /** `aria-label` for the (×) close button. */
    closeLabel: string;
    submitLabel: string;
  };
  type: {
    /** Visible label for the type radiogroup, and its accessible name. */
    groupLabel: string;
    /** Segmented-control labels, one per `RequestType`. */
    options: Record<RequestType, string>;
  };
  priority: {
    /** Visible label for the priority radiogroup, and its accessible name. */
    groupLabel: string;
    /** Segmented-control labels, one per `RequestPriority`. */
    options: Record<RequestPriority, string>;
  };
  fields: {
    description: { label: string; placeholder: string };
  };
  /** Shown next to the description once it's been touched and left empty. */
  validation: {
    descriptionRequired: string;
  };
  attachments: {
    /** Section heading above the file picker. */
    heading: string;
    /** Visible + accessible label for the "add files" control (also the `<input>`'s `<label>`). */
    addLabel: string;
    /** `aria-label` for a single attachment's remove (×) button, given its file name. */
    removeLabel: (fileName: string) => string;
    /** Per-file validation failure reasons, shown next to a rejected selection. */
    errors: {
      unsupportedType: string;
      /** Keep this in sync with `MAX_FILE_SIZE_BYTES` in `attachments/mime.ts` (10 MB). */
      tooLarge: string;
    };
  };
  /** The capture that leads the modal, and the markers dropped on it (I19222). */
  capture: {
    heading: string;
    /** Instruction under the image, explaining how a marker is placed. */
    instructions: string;
    /** Replaces the instruction once no more marks may be added. */
    pinLimitHint: string;
    /** `aria-label` for the image surface itself. */
    canvasLabel: string;
    addPinLabel: string;
    retakeLabel: string;
    /** Shown on Retake while a fresh capture is in flight. */
    retakingLabel: string;
    removeLabel: string;
    pinPlaceholder: string;
    /** `aria-label` for one mark's note field, given its 1-based number. */
    pinNoteLabel: (index: number) => string;
    removePinLabel: (index: number) => string;
    /** Shown when a retake discarded marks placed on the image it replaced. */
    pinsClearedNotice: string;
    /** Beside the turning square while the screenshot behind the open modal is taken. */
    pendingLabel: string;
  };
  /** Screen recording (I19222, ADR 0030). */
  recording: {
    startLabel: string;
    /** The confirm step's sentence, naming what is captured and for how long. */
    explainer: (maxSeconds: number) => string;
    confirmLabel: string;
    cancelLabel: string;
    stopLabel: string;
    /** Elapsed against the budget, shown beside the meter. */
    elapsed: (seconds: number, maxSeconds: number) => string;
    /** Shown when the reporter refused the share, or the browser could not start one. */
    errorMessage: string;
    /** File name stem for the finished clip. */
    fileLabel: string;
  };
  screenshot: {
    /** Button in the modal's Attachments section that kicks off capture. */
    captureButtonLabel: string;
    /** Shown on the capture button while `capturer.capture()` is in flight. */
    capturingLabel: string;
    /** Shown (as an alert) when `capturer.capture()` rejects. */
    captureErrorMessage: string;
    /** Shown (as an alert) when applying manual redaction boxes fails on confirm. */
    redactionErrorMessage: string;
    /** Heading/instructions shown above the captured image in `ScreenshotPreview`. */
    previewHeading: string;
    instructions: string;
    /** `aria-label` for the image/drag surface itself. */
    canvasLabel: string;
    /** `aria-label` for a single drawn redaction box, given its 1-based index. */
    boxLabel: (index: number) => string;
    clearBoxesLabel: string;
    confirmLabel: string;
    cancelLabel: string;
  };
  submit: {
    /** Shown on the submit button (replacing `modal.submitLabel`) while the request is in flight. */
    sendingLabel: string;
    /**
     * Success step message, given the backend-assigned reference (e.g. "FB-142"). A host
     * whose send produced no reference (Works, while its Feedback Manager leg is off) gets
     * the sentence without one rather than "Your report is ."
     */
    successMessage: (reference: string) => string;
    /** Button that closes the modal from the success step. */
    doneLabel: string;
    /** Success-step link to the report in the client portal, shown only when the application has one. */
    trackLabel: string;
    /** Shown (as an alert) above the footer for a `RateLimitError`. */
    rateLimitedMessage: string;
    /** Shown (as an alert) above the footer for any other submit failure. The form stays filled in. */
    errorMessage: string;
    /** The submit button's label after a failed send. */
    retryLabel: string;
  };
}

export const strings: Strings = {
  tooltip: {
    defaultText: 'Click to submit feedback or a new request',
  },
  modal: {
    heading: 'Send Feedback',
    subheading: 'Every report gets a reference you can quote.',
    closeLabel: 'Close',
    submitLabel: 'Send Feedback',
  },
  type: {
    groupLabel: 'Type',
    options: {
      BUG: 'Problem',
      FEATURE: 'Idea',
      FEEDBACK: 'Praise',
      // Never offered. `presentType` opens a suggestion as an idea.
      SUGGESTION: 'Idea',
    },
  },
  priority: {
    groupLabel: 'Priority',
    options: {
      LOW: 'Low',
      NORMAL: 'Normal',
      HIGH: 'High',
      CRITICAL: 'Critical',
    },
  },
  fields: {
    description: {
      label: 'Description',
      placeholder: 'What happened, or what would make this better?',
    },
  },
  validation: {
    descriptionRequired: 'Add a description before sending.',
  },
  attachments: {
    heading: 'Attachments',
    addLabel: 'Add Files',
    removeLabel: (fileName) => `Remove ${fileName}`,
    errors: {
      unsupportedType: 'This file type is not supported.',
      tooLarge: 'File is too large (10 MB max).',
    },
  },
  capture: {
    heading: 'Screenshot',
    instructions: 'Click the image to mark a spot, then say what is wrong with it.',
    pinLimitHint: 'That is as many marks as one screenshot can carry.',
    canvasLabel: 'Captured screenshot. Click to mark a spot.',
    addPinLabel: 'Add Mark',
    retakeLabel: 'Retake',
    retakingLabel: 'Retaking…',
    removeLabel: 'Remove',
    pinPlaceholder: 'What is wrong here?',
    pinNoteLabel: (index) => `Note for mark ${index}`,
    removePinLabel: (index) => `Remove mark ${index}`,
    pinsClearedNotice: 'Your marks were cleared, because they pointed at the screenshot this one replaced.',
    pendingLabel: 'Taking a screenshot…',
  },
  recording: {
    startLabel: 'Record Screen',
    explainer: (maxSeconds) =>
      `Your browser will ask which window or tab to share, and whether to use your microphone. Recording stops on its own after ${maxSeconds} seconds.`,
    confirmLabel: 'Choose What to Share',
    cancelLabel: 'Cancel',
    stopLabel: 'Stop Recording',
    elapsed: (seconds, maxSeconds) => `${seconds}s of ${maxSeconds}s`,
    errorMessage: 'No recording was made. You can still submit without one.',
    fileLabel: 'screen-recording',
  },
  screenshot: {
    captureButtonLabel: 'Capture Screenshot',
    capturingLabel: 'Capturing…',
    captureErrorMessage: 'Could not capture a screenshot. You can still submit without one.',
    redactionErrorMessage: 'Could not apply your redactions. Please try again or discard the screenshot.',
    previewHeading: 'Review Screenshot',
    instructions: 'Drag over the image to black out anything sensitive before attaching it.',
    canvasLabel: 'Captured screenshot. Drag to add a redaction box.',
    boxLabel: (index) => `Redaction box ${index}`,
    clearBoxesLabel: 'Clear Redaction Boxes',
    confirmLabel: 'Use Screenshot',
    cancelLabel: 'Discard',
  },
  submit: {
    sendingLabel: 'Sending…',
    successMessage: (reference) =>
      reference ? `Thanks! Your report is ${reference}.` : 'Thanks! Your feedback was sent.',
    doneLabel: 'Done',
    trackLabel: 'Track This Report',
    rateLimitedMessage: "You're sending too fast. Try again in a moment.",
    errorMessage: "Couldn't send that. Your note is still here.",
    retryLabel: 'Send Again',
  },
};
